import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChangeEvent, useEffect, useState } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import { useSendEmailMutation } from "../../services/email.service";
import { Send } from "@mui/icons-material";
import Joi from "joi";
import { User } from "../../types/users";
import ToastCustom from "../../hooks/ToastCustom";
import { useGetEmailTemplatesQuery } from "../../services/emailTemplates.service";

const joiSchema = Joi.object({
  to: Joi.string().email({ tlds: { allow: false } }).required(),
  subject: Joi.string().required(),
}).unknown(true);

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": { width: "100%" },
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

interface Props {
  details: any;
  open: boolean;
  currentUser: User;
  handleClose: () => void;
  refNo?: string;
}

function applyVariables(text: string, details: any, refNo?: string): string {
  return text
    .replace(/\{\{fullName\}\}/g, details?.fullName ?? "")
    .replace(/\{\{email\}\}/g, details?.email ?? "")
    .replace(/\{\{refNo\}\}/g, refNo ?? details?.refNo ?? "")
    .replace(/\{\{university\}\}/g, details?.university ?? "")
    .replace(/\{\{program\}\}/g, details?.program ?? "")
    .replace(/\{\{phone\}\}/g, details?.phone ?? "")
    .replace(/\{\{status\}\}/g, details?.status ?? "");
}

function EmailEditor({ details, open, currentUser, handleClose, refNo }: Props) {
  const [editorHtml, setEditorHtml] = useState<string>("");
  const [emailData, setEmailData] = useState<any>({
    to: details?.email ?? "",
    from: currentUser?.email ?? "",
    subject: "",
  });
  const [valid, setValid] = useState(false);
  const [sendEmail, { isLoading }] = useSendEmailMutation();
  const { data: templates = [] } = useGetEmailTemplatesQuery();
  const [templateAnchor, setTemplateAnchor] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    const { error } = joiSchema.validate(emailData, { abortEarly: false });
    setValid(!error);
  }, [emailData]);

  // Reset when dialog opens for a new recipient
  useEffect(() => {
    if (open) {
      setEmailData({ to: details?.email ?? "", from: currentUser?.email ?? "", subject: "" });
      setEditorHtml("");
    }
  }, [open, details?.email, currentUser?.email]);

  const handleSend = async () => {
    try {
      await sendEmail({
        message: {
          from: emailData.from,
          to: emailData.to,
          text: "",
          html: editorHtml,
          subject: emailData.subject,
        },
        createdBy: currentUser._id,
        status: "sent",
        refNo,
      }).unwrap();
      ToastCustom.success("Email sent successfully");
      handleClose();
    } catch {
      // error toast handled by global RTK error middleware
    }
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setEmailData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleUseTemplate = (templateId: string) => {
    const t = templates.find((t) => t._id === templateId);
    if (!t) return;
    setEmailData((prev: any) => ({
      ...prev,
      subject: applyVariables(t.subject, details, refNo),
    }));
    setEditorHtml(applyVariables(t.html, details, refNo));
    setTemplateAnchor(null);
  };

  return (
    <StyledDialog open={open}>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        Send Email{refNo ? ` · ${refNo}` : ""}
        {templates.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            sx={{ ml: "auto", mr: 5, fontSize: "0.75rem" }}
            onClick={(e) => setTemplateAnchor(e.currentTarget)}
          >
            Use Template
          </Button>
        )}
      </DialogTitle>
      <Popover
        open={!!templateAnchor}
        anchorEl={templateAnchor}
        onClose={() => setTemplateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 280, maxHeight: 360 } }}
      >
        <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: "block", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Templates
        </Typography>
        <Divider />
        <List dense disablePadding>
          {templates.map((t) => (
            <ListItemButton key={t._id} onClick={() => handleUseTemplate(t._id)}>
              <ListItemText
                primary={t.name}
                secondary={t.subject}
                primaryTypographyProps={{ fontWeight: 500, fontSize: "0.875rem" }}
                secondaryTypographyProps={{ noWrap: true, fontSize: "0.75rem" }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
      <IconButton
        onClick={handleClose}
        aria-label="close"
        sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Grid container spacing={1} py={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              id="subject"
              value={emailData.subject}
              onChange={handleFormChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="From"
              name="from"
              id="from"
              value="info@5milah.com"
              InputProps={{ readOnly: true }}
              helperText="Sent via 5milah mail server"
              sx={{ "& input": { color: "text.secondary" } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="To"
              name="to"
              id="to"
              value={emailData.to}
              onChange={handleFormChange}
            />
          </Grid>
        </Grid>
        <Paper>
          <Editor
            style={{ minHeight: 120 }}
            value={editorHtml}
            onTextChange={(e: EditorTextChangeEvent) => setEditorHtml(e.htmlValue ?? "")}
          />
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || isLoading}
          endIcon={isLoading ? <CircularProgress size={14} /> : <Send />}
          onClick={handleSend}
        >
          {isLoading ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default EmailEditor;
